import React from "react";
import { useTranslation } from "react-i18next";
import {
  AdminAcquisition,
  acquisitionConversionPercent,
  acquisitionEngagementPercent,
} from "../../features/admin/models/AdminAcquisition";

interface AdminAcquisitionSummaryProps {
  acquisition: AdminAcquisition;
}

interface SummaryTile {
  label: string;
  value: string;
  /** The Meta-ad share of the value, when the step can tell where a visitor came from. */
  metaCount?: number;
  /** A second muted line, for a step whose rate matters more than its count. */
  detail?: string;
}

const formatPercent = (value: number | null): string => (value === null ? "—" : `${value}%`);

/** The funnel totals for the selected range: views, engaged reads, taps, arrivals, trial links, accounts. */
const AdminAcquisitionSummary: React.FC<AdminAcquisitionSummaryProps> = ({ acquisition }) => {
  const { t } = useTranslation();
  const engagementPercent = acquisitionEngagementPercent(
    acquisition.totalPageViews,
    acquisition.totalEngaged
  );
  const tiles: SummaryTile[] = [
    {
      label: t("admin.acquisition.pageViews"),
      value: String(acquisition.totalPageViews),
      metaCount: acquisition.totalMetaPageViews,
    },
    {
      label: t("admin.acquisition.engaged"),
      value: String(acquisition.totalEngaged),
      metaCount: acquisition.totalMetaEngaged,
      detail:
        engagementPercent === null
          ? undefined
          : t("admin.acquisition.engagedRate", { percent: engagementPercent }),
    },
    {
      label: t("admin.acquisition.ctaTaps"),
      value: String(acquisition.totalCtaTaps),
      metaCount: acquisition.totalMetaCtaTaps,
    },
    {
      label: t("admin.acquisition.arrivals"),
      value: String(acquisition.totalArrivals),
      metaCount: acquisition.totalMetaArrivals,
    },
    {
      label: t("admin.acquisition.trialLinksRequested"),
      value: String(acquisition.totalTrialLinksRequested),
      metaCount: acquisition.totalMetaTrialLinksRequested,
    },
    {
      label: t("admin.acquisition.trialLinksRedeemed"),
      value: String(acquisition.totalTrialLinksRedeemed),
      metaCount: acquisition.totalMetaTrialLinksRedeemed,
    },
    { label: t("admin.acquisition.signUps"), value: String(acquisition.totalSignUps) },
    {
      label: t("admin.acquisition.conversion"),
      value: formatPercent(
        acquisitionConversionPercent(
          acquisition.totalArrivals,
          acquisition.totalSignUpsSinceArrivalCounter
        )
      ),
      detail: t("admin.acquisition.conversionSinceCounter"),
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {tiles.map((tile) => (
        <div key={tile.label} className="with-border rounded-2xl p-4 lg:p-6">
          <p className="text-sm text-grey-40 dark:text-grey-70">{tile.label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-grey-10 dark:text-white">
            {tile.value}
          </p>
          {tile.metaCount !== undefined && (
            <p className="mt-1 text-xs text-grey-50 dark:text-grey-60">
              {t("admin.acquisition.fromMeta", { count: tile.metaCount })}
            </p>
          )}
          {tile.detail && (
            <p className="mt-0.5 text-xs text-grey-50 dark:text-grey-60">{tile.detail}</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminAcquisitionSummary;
